/**
 * ConversionRateBand — TICKET 5
 *
 * "Conversion rates at every step as the locked design pattern, with
 *  'eliminate or convert' as the core principle."
 *  — work/projects/aip/dashboard_vision.md
 *
 * Shared band that renders the staged-conversion strip on every role
 * dashboard. Pulls aggregated funnel counts + rates from
 * GET /api/conversion-rates. Scope is a prop:
 *
 *   scope="self"                  → AE personal funnel, BDR personal funnel.
 *                                   Server forbids team views from non-managers.
 *   scope="team"                  → Manager/Executive team funnel + per-rep drilldown.
 *   scope="tenant_top_of_funnel"  → Marketing top-of-funnel (outreach → response
 *                                   → MQL handoff), tenant-wide.
 *
 * Role prop controls which stage labels render (BDR shows dials→handoff,
 * Marketing shows outreach→MQL, AE shows conversations→closed_won, Exec
 * shows full funnel).
 *
 * Window pill (Today/Week/Month/Quarter/Year) reuses the existing
 * per-user localStorage pattern under a `mi6.conversion.window.{role}` key.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/session";

export type ConversionWindow = "today" | "week" | "month" | "quarter" | "year";
export type ConversionScope = "self" | "team" | "tenant_top_of_funnel";
export type ConversionRole = "ae" | "bdr" | "marketing" | "exec";

interface Stage {
  key: string;
  label: string;
  count: number;
}
interface Rate {
  from: string;
  to: string;
  rate: number;
  numerator: number;
  denominator: number;
}
interface RepRow {
  user_id: string;
  name: string;
  stages: Stage[];
  rates: Rate[];
}
interface ConversionResponse {
  window: ConversionWindow;
  window_start: string;
  window_end: string;
  scope: ConversionScope;
  role: ConversionRole;
  stages: Stage[];
  rates: Rate[];
  per_rep?: RepRow[];
}

interface Props {
  scope: ConversionScope;
  role: ConversionRole;
  /** Defaults to "week", or the persisted per-role choice. */
  initialWindow?: ConversionWindow;
  /** Optional title override. */
  title?: string;
  /** Pre-loaded payload (for tests or SSR). */
  data?: ConversionResponse;
}

function windowKey(role: ConversionRole): string {
  return `mi6.conversion.window.${role}`;
}
function readWindow(role: ConversionRole, fallback: ConversionWindow = "week"): ConversionWindow {
  try {
    const v = localStorage.getItem(windowKey(role));
    if (v && ["today","week","month","quarter","year"].includes(v)) return v as ConversionWindow;
  } catch { /* private mode */ }
  return fallback;
}
function writeWindow(role: ConversionRole, v: ConversionWindow): void {
  try { localStorage.setItem(windowKey(role), v); } catch { /* ignore */ }
}

function fmtPct(rate: number): string {
  if (!isFinite(rate) || rate <= 0) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}

const WINDOW_OPTS: { id: ConversionWindow; label: string }[] = [
  { id: "today",   label: "Today"   },
  { id: "week",    label: "Week"    },
  { id: "month",   label: "Month"   },
  { id: "quarter", label: "Quarter" },
  { id: "year",    label: "Year"    },
];

export default function ConversionRateBand({
  scope,
  role,
  initialWindow,
  title,
  data: preloaded,
}: Props) {
  const [win, setWin] = useState<ConversionWindow>(() => initialWindow ?? readWindow(role));
  const [data, setData] = useState<ConversionResponse | null>(preloaded ?? null);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preloaded) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<ConversionResponse>(
      `/api/conversion-rates?scope=${scope}&role=${role}&window=${win}`,
    )
      .then((r) => {
        if (cancelled) return;
        setLoading(false);
        if (!r) setError("No data");
        else setData(r);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setLoading(false);
        const msg = e?.message ?? "Failed to load conversion rates";
        // Permission denials (AE asking for team scope) should be visible,
        // not silently empty.
        setError(msg);
        console.error("[ConversionRateBand] load failed:", e);
      });
    return () => { cancelled = true; };
  }, [scope, role, win, preloaded]);

  const stages = data?.stages ?? [];
  const rates = data?.rates ?? [];

  const displayTitle = title ?? defaultTitle(role, scope);

  return (
    <div
      data-testid="conversion-rate-band"
      data-scope={scope}
      data-role={role}
      style={{
        background: "white",
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: 10,
        padding: "10px 14px",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0B0B0F", fontFamily: "DM Sans, sans-serif" }}>
            {displayTitle}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            Eliminate or convert — track every step.
          </div>
        </div>
        <div role="tablist" aria-label="Conversion window" style={{ display: "inline-flex", gap: 2, background: "#f1f5f9", padding: 2, borderRadius: 999 }}>
          {WINDOW_OPTS.map((w) => (
            <button
              key={w.id}
              role="tab"
              aria-selected={win === w.id}
              data-testid={`conversion-window-${w.id}`}
              onClick={() => { setWin(w.id); writeWindow(role, w.id); }}
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                color: win === w.id ? "#0B0B0F" : "#64748b",
                background: win === w.id ? "white" : "transparent",
                border: "none",
                cursor: "pointer",
                boxShadow: win === w.id ? "0 1px 2px rgba(15,23,42,0.06)" : "none",
              }}
            >{w.label}</button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ color: "#991b1b", background: "#fef2f2", padding: 8, borderRadius: 8, fontSize: 12 }} data-testid="conversion-error">
          {error}
        </div>
      )}

      {loading && !error && (
        <div style={{ color: "#64748b", fontSize: 12 }}>Loading conversion rates...</div>
      )}

      {!loading && !error && stages.length > 0 && stages.some((s) => s.count > 0) && (
        <div style={{ display: "flex", alignItems: "stretch", gap: 8, overflowX: "auto" }}>
          {stages.map((s, i) => (
            <div key={s.key} style={{ display: "flex", alignItems: "stretch", gap: 8 }}>
              <StageCell stage={s} />
              {i < stages.length - 1 && <RateArrow rate={rates[i]} />}
            </div>
          ))}
        </div>
      )}

      {!loading && !error && (stages.length === 0 || stages.every((s) => s.count === 0)) && (
        <div
          data-testid="conversion-empty"
          style={{ color: "#525258", fontSize: 12, padding: "12px 0" }}
        >
          No activity yet for this period.
        </div>
      )}

      {!loading && !error && data?.per_rep && data.per_rep.length > 0 && (
        <details style={{ marginTop: 8 }} data-testid="conversion-per-rep">
          <summary style={{ fontSize: 12, color: "#475569", cursor: "pointer" }}>
            Per-rep breakdown ({data.per_rep.length})
          </summary>
          <div style={{ marginTop: 8, overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#64748b" }}>
                  <th style={{ padding: "4px 8px" }}>Rep</th>
                  {stages.map((s) => <th key={s.key} style={{ padding: "4px 8px" }}>{s.label}</th>)}
                  {rates.map((r) => <th key={`${r.from}-${r.to}`} style={{ padding: "4px 8px" }}>{r.from}→{r.to}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.per_rep.map((rep) => (
                  <tr key={rep.user_id} data-testid={`conversion-rep-${rep.user_id}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px", fontWeight: 600, color: "#0B0B0F" }}>{rep.name}</td>
                    {rep.stages.map((s) => (
                      <td key={s.key} style={{ padding: "4px 8px", fontVariantNumeric: "tabular-nums" }}>{s.count.toLocaleString()}</td>
                    ))}
                    {rep.rates.map((r) => (
                      <td key={`${r.from}-${r.to}`} style={{ padding: "4px 8px", color: rateColor(r.rate) }}>{fmtPct(r.rate)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

function StageCell({ stage }: { stage: Stage }) {
  return (
    <div
      data-testid={`conversion-stage-${stage.key}`}
      style={{
        minWidth: 110,
        padding: "8px 12px",
        borderRadius: 10,
        background: "#FAFAF7",
        border: "1px solid rgba(15,23,42,0.06)",
      }}
    >
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>{stage.label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#0B0B0F", fontVariantNumeric: "tabular-nums" }}>
        {stage.count.toLocaleString()}
      </div>
    </div>
  );
}

function RateArrow({ rate }: { rate?: Rate }) {
  if (!rate) return null;
  const pct = fmtPct(rate.rate);
  return (
    <div
      data-testid={`conversion-rate-${rate.from}-${rate.to}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "0 4px",
        color: rateColor(rate.rate),
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      <span aria-hidden="true">→</span>
      <span>{pct}</span>
    </div>
  );
}

function rateColor(r: number): string {
  if (!isFinite(r) || r <= 0) return "#64748b";
  if (r >= 0.5)  return "#166534";  // green
  if (r >= 0.15) return "#854d0e";  // amber
  return "#991b1b";                  // red
}

function defaultTitle(role: ConversionRole, scope: ConversionScope): string {
  if (scope === "tenant_top_of_funnel") return "Top-of-funnel conversion";
  if (scope === "team") return "Team conversion funnel";
  if (role === "ae") return "Your conversion funnel";
  if (role === "bdr") return "Your hunt-list funnel";
  if (role === "marketing") return "Top-of-funnel conversion";
  return "Conversion funnel";
}
